import { XMarkIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ChartsService } from "../services/charts-service";
import FileDisplay from "./file-display";
import FileUpload from "./file-upload";

type CreateTestDeckPopupProps = {
  chartId: string;
  open: boolean;
  reuploading?: boolean;
  close: () => void;
};

export const CreateTestDeckPopup = (props: CreateTestDeckPopupProps) => {
  const [squareMarks, setSquareMarks] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [startPage, setStartPage] = useState<string>();
  const [endPage, setEndPage] = useState<string>();

  const addFiles = (files: File[]) => {
    setPdfFiles(files);
  };

  const removeFiles = () => {
    setPdfFiles([]);
  };

  const closeModal = () => {
    removeFiles();
    setSquareMarks(false);
    props.close();
  };

  const uploadPdf = async () => {
    for (const file of pdfFiles) {
      setUploading(true);
      try {
        await ChartsService.adminUploadPdf(props.chartId, squareMarks, file, startPage, endPage);
        removeFiles();
        navigate(`/mark-ballot/${props.chartId}`);
      } catch (e: any) {
        console.log(e);
        toast.error(e?.response?.data || "Error uploading PDF");
        setUploading(false);
      } finally {
        setUploading(false);
        if (props.reuploading) closeModal();
      }
    }
  };

  return (
    <dialog open={props.open} className="fixed inset-0 w-full px-4 pt-5 pb-4 overflow-y-auto text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl z-100 sm:my-8 sm:align-middle sm:p-6 sm:w-full md:w-3/4 lg:w-1/2 xl:w-2/3 2xl:w-2/5">
      <div className="flex flex-col w-full">
        <div className="flex flex-row justify-end w-full">
          <XMarkIcon
            height={30}
            width={30}
            className={"text-primary-blue cursor-pointer"}
            onClick={() => closeModal()}
          />
        </div>
        {!uploading && (
          <>
            <div className="flex flex-col items-center justify-center">
              <p className="mb-4 text-2xl font-bold uppercase text-primary-blue font-poppins">
                upload a pdf ballot
              </p>
              {pdfFiles.length === 0 && (
                <p className="mb-6 text-lg text-gray-500">
                  Choose a ballot to upload.
                </p>
              )}
            </div>
            {pdfFiles.length === 0 ? (
              <FileUpload
                filesAdded={addFiles}
                multiple={false}
                accept="application/pdf"
                fileDisplay="PDF"
              />
            ) : (
              <div className="flex justify-center">
                <FileDisplay file={pdfFiles[0]} removeFile={removeFiles} />
              </div>
            )}
            <div className="flex flex-row mt-6 xl:justify-between">
              <div className='flex flex-row items-center w-1/2 mt-6 min-w-1/4'>
                <input
                  className="mr-2"
                  type="checkbox"
                  id="square-marks"
                  checked={squareMarks}
                  onChange={() => setSquareMarks(!squareMarks)}
                />
                <p>Use square marks</p>
              </div>
              {pdfFiles.length > 0 &&
                <div className='flex flex-row justify-end w-full'>
                  <div className='w-1/2'>
                    <label
                      htmlFor="start page number"
                      className="font-medium tracking-widest uppercase"
                    >
                      Start page #
                    </label>
                    <input
                      id='start page number'
                      className="w-3/4 px-4 py-3 mt-2 border rounded-md border-primary-blue"
                      type="number"
                      placeholder="page #"
                      onChange={(e) => setStartPage(e.target.value)}
                    />
                  </div>
                  <div className='w-1/2'>
                    <label
                      htmlFor="end page number"
                      className="font-medium tracking-widest uppercase"
                    >
                      End page #
                    </label>
                    <input
                      id='end page number'
                      className="w-3/4 px-4 py-3 mt-2 border rounded-md border-primary-blue"
                      type="number"
                      placeholder="page #"
                      onChange={(e) => setEndPage(e.target.value)}
                    />
                  </div>
                </div>
              }
            </div>
            <div className="flex flex-row gap-2 mt-6 align-middle">
              <button
                onClick={() => {
                  closeModal();
                }}
                className="w-full inline-flex uppercase tracking-[0.07rem] justify-center items-center px-6 py-3 text-primary-blue border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                cancel
              </button>
              <button
                disabled={!pdfFiles.length}
                onClick={uploadPdf}
                className={`${!pdfFiles.length && "opacity-20"
                  } uppercase w-full inline-flex tracking-[0.07rem] justify-center items-center px-6 py-3 text-white text-base font-semibold rounded-md shadow-sm bg-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`}
              >
                upload file
              </button>
            </div>
          </>
        )}
        {uploading && (
          <div className="flex flex-col items-center justify-center">
            <p className="mb-4 text-2xl font-bold uppercase text-primary-blue font-poppins">
              Uploading...
            </p>
            <p className="mb-6 text-lg text-gray-500">
              Please wait while your ballot is uploaded.
            </p>
          </div>
        )}
      </div>
    </dialog>
  );
};
